import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Globe, Check, Search, X } from 'lucide-react';
import { getAllTimezones, getTimezoneDisplay, getTimezoneOffset } from '../../utils/timezone';
import { useDoctorData } from '../../context/DoctorDataContext';

const GlobalTimezoneSelector: React.FC = () => {
  const { doctorTimezone, updateTimezone } = useDoctorData();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get all timezones and format them
  const allTimezones = useMemo(() => {
    const tzList = getAllTimezones();
    return tzList.map(tz => ({
      value: tz,
      ...getTimezoneDisplay(tz)
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter timezones based on search
  const filteredTimezones = useMemo(() => {
    if (!searchQuery) return allTimezones;
    
    const query = searchQuery.toLowerCase();
    return allTimezones.filter(tz => 
      tz.label.toLowerCase().includes(query) ||
      tz.value.toLowerCase().includes(query) ||
      tz.offset.toLowerCase().includes(query)
    );
  }, [allTimezones, searchQuery]);

  const handleTimezoneSelect = async (timezone: string) => {
    setSaving(true);
    try {
      console.log('🌍 Changing display timezone to:', timezone);
      await updateTimezone(timezone);
      
      console.log('✅ Timezone changed successfully');
      setIsOpen(false);
      setSearchQuery('');
    } catch (err: any) {
      console.error('❌ Failed to change timezone:', err);
    } finally {
      setSaving(false);
    }
  };

  // Get current timezone display
  const currentTzDisplay = getTimezoneDisplay(doctorTimezone);
  const currentOffset = getTimezoneOffset(doctorTimezone);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Timezone Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        disabled={saving}
      >
        <Globe className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">
          {doctorTimezone.split('/').pop()?.replace(/_/g, ' ') || 'Timezone'}
        </span>
        <span className="text-xs text-gray-500">
          ({currentOffset})
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          {/* Header */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">Select Timezone</h3>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search city, country, or offset..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Timezone List */}
          <div className="max-h-80 overflow-y-auto">
            {filteredTimezones.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No timezones found matching "{searchQuery}"
              </div>
            ) : (
              <div className="py-1">
                {filteredTimezones.map((tz) => (
                  <button
                    key={tz.value}
                    onClick={() => handleTimezoneSelect(tz.value)}
                    disabled={saving}
                    className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                      tz.value === doctorTimezone ? 'bg-blue-50' : ''
                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {tz.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {tz.value} • {tz.offset}
                      </div>
                    </div>
                    {tz.value === doctorTimezone && (
                      <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-600">
              <strong>Current:</strong> {currentTzDisplay.label} ({currentOffset})
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Showing {filteredTimezones.length} of {allTimezones.length} timezones
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalTimezoneSelector;
